import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import { ARTICLES } from "../data/articles";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

const PAGE_SIZE = 6;

export default function Blog() {
  const [category, setCategory] = useState("Semua");
  const [page, setPage] = useState(1);

  const categories = useMemo(
    () => ["Semua", ...Array.from(new Set(ARTICLES.map((a) => a.category)))],
    []
  );

  const filtered = useMemo(
    () =>
      category === "Semua"
        ? ARTICLES
        : ARTICLES.filter((a) => a.category === category),
    [category]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const [featured, ...rest] = filtered;

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog ListrikBatam.com — Tips & Panduan Listrik"
        description="Artikel lengkap seputar listrik: tips hemat energi, panduan instalasi, standar grounding, panel surya, dan edukasi kelistrikan rumah tangga serta industri."
        canonical="https://listrikbatam.com/blog"
      />

      <section className="relative bg-[#F8FAFC] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-24 pb-16">
          <Reveal>
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Blog
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black leading-[0.98] tracking-tight text-[#0F4C75] max-w-4xl text-balance">
              Tips, panduan, <br />
              & edukasi <span className="text-[#FF6B00]">kelistrikan.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[#1B262C]/75 text-base md:text-lg">
              Ilmu praktis yang kami kumpulkan dari ratusan proyek di Batam &
              Kepri — supaya Anda tidak perlu belajar dari kesalahan mahal.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Featured */}
      {featured && safePage === 1 && category === "Semua" && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-16">
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`featured-article-${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center rounded-3xl bg-white border border-[#E5E7EB] p-5 md:p-8 hover:border-[#3282B8] transition-all"
            >
              <div className="lg:col-span-6 rounded-2xl overflow-hidden bg-[#0F4C75] aspect-[4/3] lg:aspect-[16/12]">
                <img
                  src={featured.cover}
                  alt={featured.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF6B00]">
                  <Tag className="w-3 h-3" /> {featured.category}
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight">
                  {featured.title}
                </h2>
                <p className="mt-4 text-[#1B262C]/75 md:text-lg max-w-xl">
                  {featured.excerpt}
                </p>
                <div className="mt-6 flex items-center gap-5 text-xs text-[#1B262C]/60">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {featured.readingTime}
                  </span>
                  <span>{new Date(featured.date).toLocaleDateString("id-ID", {day:"numeric", month:"long", year:"numeric"})}</span>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-[#FF6B00] font-bold text-sm">
                  Baca artikel <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-6 flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => {
              setCategory(c);
              setPage(1);
            }}
            data-testid={`category-${c.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
              category === c
                ? "bg-[#0F4C75] text-white border-[#0F4C75]"
                : "bg-white text-[#1B262C] border-[#E5E7EB] hover:border-[#3282B8]"
            }`}
          >
            {c}
          </button>
        ))}
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-10">
        <Reveal
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          stagger
        >
          {(category === "Semua" && safePage === 1
            ? rest.slice(0, PAGE_SIZE)
            : paginated
          ).map((a) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="group rounded-3xl bg-white border border-[#E5E7EB] overflow-hidden hover:-translate-y-1 hover:border-[#3282B8] hover:shadow-[0_20px_40px_-20px_rgba(15,76,117,0.3)] transition-all flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#F8FAFC]">
                <img
                  src={a.cover}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF6B00] mb-3">
                  {a.category}
                </div>
                <h3 className="font-display text-xl font-bold text-[#0F4C75] leading-tight">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm text-[#1B262C]/70 line-clamp-3">
                  {a.excerpt}
                </p>
                <div className="mt-auto pt-5 flex items-center justify-between text-xs text-[#1B262C]/60">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> {a.readingTime}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-[#FF6B00] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </Link>
          ))}
        </Reveal>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center items-center gap-2">
            <button
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="pagination-prev"
              className="w-10 h-10 rounded-full border border-[#E5E7EB] bg-white disabled:opacity-40 hover:border-[#3282B8] transition flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                data-testid={`pagination-page-${i + 1}`}
                className={`min-w-10 h-10 px-3 rounded-full text-sm font-semibold transition ${
                  safePage === i + 1
                    ? "bg-[#0F4C75] text-white"
                    : "bg-white text-[#1B262C] border border-[#E5E7EB] hover:border-[#3282B8]"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="pagination-next"
              className="w-10 h-10 rounded-full border border-[#E5E7EB] bg-white disabled:opacity-40 hover:border-[#3282B8] transition flex items-center justify-center"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
